import { motion } from 'framer-motion';
import { UserPlus, Users, TrendingUp, Building2, Calendar } from 'lucide-react';
import KPICard from './KPICard';
import RecentHires from './RecentHires';
import QuickActions from './QuickActions';
import EmployeeGrowthChart from './EmployeeGrowthChart';
import DepartmentDonutChart from '../charts/DepartmentDonutChart';
import EmptyState from '../ui/EmptyState';

function Home({ 
  employees = [], 
  onAddEmployee, 
  onViewEmployees, 
  onExportCSV 
}) {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Calculate KPIs
  const totalEmployees = employees.length;
  const newThisMonth = employees.filter(emp => emp.__isNew).length;
  const activeStaff = totalEmployees; // In real app, this would filter by status
  const departments = new Set(employees.map(emp => emp.company?.name).filter(Boolean)).size;

  const hasData = employees.length > 0;

  const handleExportCSV = () => {
    if (employees.length === 0) {
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Company', 'City'];
    const rows = employees.map(emp => [
      emp.name || '',
      emp.email || '',
      emp.phone || '',
      emp.company?.name || '',
      emp.address?.city || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* 0) HERO / WELCOME SECTION */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-8 md:p-12 shadow-2xl"
      >
        {/* Radial background glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
          >
            Welcome to Employee Management System
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-xl md:text-2xl text-white/90 mb-8"
          >
            Streamlining People and Processes
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onAddEmployee}
              className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Add Employee
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onViewEmployees}
              className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg font-semibold hover:bg-white/20 transition-colors"
            >
              View Employees
            </motion.button>
          </motion.div>
        </div>
      </motion.div>

      {/* 1) HEADER ROW */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome back 👋
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {formattedDate}
        </p>
      </motion.div>

      {hasData ? (
        <>
          {/* 2) KPI CARDS GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Total Employees"
              value={totalEmployees}
              icon={Users}
              trend={12}
              trendLabel="vs last month"
              gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
              delay={0.1}
            />
            <KPICard
              title="New This Month"
              value={newThisMonth}
              icon={TrendingUp}
              trend={8}
              trendLabel="vs last month"
              gradient="bg-gradient-to-r from-purple-500 to-pink-500"
              delay={0.2}
            />
            <KPICard
              title="Active Staff"
              value={activeStaff}
              icon={Users}
              trend={5}
              trendLabel="vs last month"
              gradient="bg-gradient-to-r from-green-500 to-emerald-500"
              delay={0.3}
            />
            <KPICard
              title="Departments"
              value={departments}
              icon={Building2}
              trend={2}
              trendLabel="vs last month"
              gradient="bg-gradient-to-r from-orange-500 to-red-500"
              delay={0.4}
            />
          </div>

          {/* 3) CHARTS SECTION */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <EmployeeGrowthChart employees={employees} />
            <DepartmentDonutChart employees={employees} />
          </div>

          {/* 4) RECENT HIRES & 5) QUICK ACTIONS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RecentHires employees={employees} />
            <QuickActions
              onAddEmployee={onAddEmployee}
              onViewEmployees={onViewEmployees}
              onExportCSV={handleExportCSV}
            />
          </div>
        </>
      ) : (
        /* 6) EMPTY STATE */
        <EmptyState
          icon={Users}
          title="No employees yet"
          description="Get started by adding your first employee to the system."
          actionLabel="Add Employee"
          onAction={onAddEmployee}
        />
      )}
    </div>
  );
}

export default Home;
